import { Star, ExternalLink, Quote } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader } from "./Reveal";

const Stars = ({ value = 5, size = 16 }) => (
    <div className="relative inline-flex" aria-label={`${value} de 5 estrellas`}>
        <div className="flex gap-0.5 text-zinc-700">
            {[...Array(5)].map((_, i) => (
                <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
            ))}
        </div>
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${(value / 5) * 100}%` }}>
            <div className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, i) => (
                    <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
                ))}
            </div>
        </div>
    </div>
);

export default function Testimonials() {
    const { content, t } = useSite();
    const rating = parseFloat(content?.google_rating || "4.6") || 4.6;
    const count = content?.google_count || "7";
    const testimonials = content?.testimonials || [];

    return (
        <section id="testimonios" data-testid="testimonials-section" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <SectionHeader eyebrow={t.testimonials.eyebrow} title={t.testimonials.title} />

                <div className="mt-14 grid lg:grid-cols-3 gap-8">
                    <Reveal data-testid="google-rating-card" className="lg:col-span-1">
                        <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-br from-elevated to-surface p-8 h-full flex flex-col justify-between gold-ring">
                            <Quote size={60} className="absolute -top-2 -right-2 text-gold/10" />
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-gold">Google Reviews</p>
                                <p className="mt-6 font-serif text-7xl text-gold-gradient leading-none" data-testid="google-rating-value">
                                    {rating}
                                </p>
                                <div className="mt-5">
                                    <Stars value={rating} size={20} />
                                </div>
                                <p className="mt-4 text-sm text-zinc-400 font-light" data-testid="google-review-count">
                                    {t.testimonials.basedOn.replace("{n}", count)}
                                </p>
                            </div>
                            <a
                                href={content?.map_link || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="google-reviews-link"
                                className="mt-8 inline-flex items-center gap-2 text-sm text-gold-light hover:text-gold transition-colors"
                            >
                                {t.testimonials.seeGoogle}
                                <ExternalLink size={14} />
                            </a>
                        </div>
                    </Reveal>

                    <div className="lg:col-span-2 grid md:grid-cols-2 gap-8">
                        {testimonials.length > 0 ? (
                            testimonials.map((t, i) => (
                                <Reveal key={t.id} delay={0.1 + i * 0.08}>
                                    <article
                                        data-testid={`testimonial-card-${t.id}`}
                                        className="h-full rounded-3xl border border-gold/15 bg-surface p-8 hover:border-gold/40 transition-colors duration-500 flex flex-col"
                                    >
                                        <Stars value={t.rating || 5} />
                                        <p className="mt-5 font-light text-zinc-300 leading-relaxed flex-1" data-testid={`testimonial-text-${t.id}`}>
                                            “{t.text}”
                                        </p>
                                        <p className="mt-6 font-serif text-lg text-amber-100" data-testid={`testimonial-name-${t.id}`}>
                                            {t.name}
                                        </p>
                                    </article>
                                </Reveal>
                            ))
                        ) : (
                            <Reveal delay={0.1} className="md:col-span-2">
                                <div
                                    data-testid="testimonials-empty"
                                    className="h-full min-h-[220px] rounded-3xl border border-dashed border-gold/20 bg-surface/50 p-10 flex flex-col items-center justify-center text-center"
                                >
                                    <Quote size={28} className="text-gold/40" />
                                    <p className="mt-5 text-zinc-300 font-light max-w-md">
                                        {t.testimonials.empty.replace("{n}", count)}
                                    </p>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
